import { queryOptions, useQuery } from '@tanstack/react-query';

import { type EnrollmentPaymentProvider } from '~entities/payment/api/get-enrollment-payment-methods';
import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export enum BillingIntent {
  NO_CHANGE = 'no_change',
  IMMEDIATE = 'immediate',
}

/** Mirrors BE `TransferCreditMode` const object. */
export const TransferCreditMode = {
  AUTO: 'auto',
  CUSTOM: 'custom',
  NONE: 'none',
} as const;
export type TransferCreditMode =
  (typeof TransferCreditMode)[keyof typeof TransferCreditMode];

/** Mirrors BE `TransferErrorCode` (shared client checks). */
export const TransferErrorCode = {
  DESTINATION_ENROLLMENT_FAILED: 'DESTINATION_ENROLLMENT_FAILED',
  DESTINATION_ENROLLMENT_MISSING: 'DESTINATION_ENROLLMENT_MISSING',
  SOURCE_TEARDOWN_FAILED: 'SOURCE_TEARDOWN_FAILED',
  CREDIT_MISMATCH: 'CREDIT_MISMATCH',
  CUSTOM_CREDIT_EXCEEDS_LIMIT: 'CUSTOM_CREDIT_EXCEEDS_LIMIT',
} as const;
export type TransferErrorCode =
  (typeof TransferErrorCode)[keyof typeof TransferErrorCode];

/** Mirrors BE `ChargeBreakdownLineCode.DISCOUNT`. */
export const ChargeBreakdownLineCode = {
  DISCOUNT: 'discount',
} as const;

interface GetRemainingCreditParams {
  studioId: string;
  currentClassId: string;
  newClassId: string;
  studentId: string;
  billingIntent?: BillingIntent;
  creditMode?: TransferCreditMode;
  customCreditAmount?: number;
  skipPaymentMethod?: boolean;
  paymentMethodId?: string;
  paymentProvider?: EnrollmentPaymentProvider;
  /** V2 opt-in destination fees (independent of tuition / proration). */
  chargeMembershipFee?: boolean;
  chargeCostumeFee?: boolean;
  /** When true, use V2 transfer preview. When false/undefined, use V1. */
  isV2?: boolean;
}

export interface TransferPreviewDiscountLine {
  label: string;
  amount: number;
}

export interface GetRemainingCreditResponse {
  remainingCredit: number;
  creditUsedForTransfer: number;
  creditToBeAddedToWallet: number;
  amountToBeCharged: number;
  currentTuitionFee: number;
  newTuitionFee: number;
  billingIntent: BillingIntent;
  prorationDisabledAtStudioLevel?: boolean;
  previewSummary: {
    description: string;
    oldClassImpact: string;
    newClassImpact: string;
  };
  /** From V2 enrollmentPreview — discounts applied to destination charge. */
  discountTotal?: number;
  discountLines?: TransferPreviewDiscountLine[];
  transferCreditAppliedFromEnrollment?: number;
  /** True when amountToBeCharged came from enrollmentPreview.amountDueTotal. */
  amountDueFromEnrollmentPreview?: boolean;
}

interface V2TransferCreditBreakdown {
  availableCredit: number;
  creditAppliedToNewClass: number;
  creditAddedToWallet: number;
  amountToBeCharged: number;
  currentTuitionFee: number;
  newTuitionFee: number;
  effectiveBillingIntent: BillingIntent;
  prorationDisabledAtStudioLevel?: boolean;
  previewSummary: {
    description: string;
    oldClassImpact: string;
    newClassImpact: string;
  };
}

interface V2EnrollmentPreviewCharge {
  discountTotal?: number;
  transferCreditApplied?: number;
  amountDue?: number;
  amountDueWithTransactionFee?: number;
  appliedDiscounts?: Array<{
    label?: string;
    amount?: number;
  }>;
  lineItems?: Array<{
    code?: string;
    label?: string;
    amount?: number;
  }>;
}

interface V2EnrollmentPreview {
  amountDueTotal?: number;
  charges?: V2EnrollmentPreviewCharge[];
}

interface V2TransferPreviewResponse {
  transferred: false;
  transferCredit: V2TransferCreditBreakdown;
  enrollmentPreview: V2EnrollmentPreview;
}

/** Round display money to 2dp without floating-point drift from Number(). */
const toMoneyAmount = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.round(parsed * 100) / 100;
};

const mapV2PreviewToRemainingCredit = (
  data: V2TransferPreviewResponse,
): GetRemainingCreditResponse => {
  const credit = data.transferCredit;
  const enrollmentPreview = data.enrollmentPreview;
  const firstCharge = enrollmentPreview?.charges?.[0];

  const discountLinesFromApplied =
    firstCharge?.appliedDiscounts
      ?.map((discount) => ({
        label: discount.label?.trim() || 'Discount',
        amount: toMoneyAmount(discount.amount),
      }))
      .filter((line) => line.amount > 0) ?? [];

  const discountLinesFromItems =
    firstCharge?.lineItems
      ?.filter(
        (item) =>
          item.code === ChargeBreakdownLineCode.DISCOUNT ||
          toMoneyAmount(item.amount) < 0,
      )
      .map((item) => ({
        label: item.label?.trim() || 'Discount',
        amount: Math.abs(toMoneyAmount(item.amount)),
      }))
      .filter((line) => line.amount > 0) ?? [];

  const discountLines =
    discountLinesFromApplied.length > 0
      ? discountLinesFromApplied
      : discountLinesFromItems;

  const discountTotal =
    firstCharge?.discountTotal ??
    discountLines.reduce((sum, line) => toMoneyAmount(sum + line.amount), 0);

  const enrollmentAmountDue =
    enrollmentPreview?.amountDueTotal ??
    firstCharge?.amountDueWithTransactionFee ??
    firstCharge?.amountDue;

  const hasEnrollmentAmount =
    typeof enrollmentAmountDue === 'number' &&
    !Number.isNaN(enrollmentAmountDue);

  return {
    remainingCredit: credit.availableCredit,
    creditUsedForTransfer: credit.creditAppliedToNewClass,
    creditToBeAddedToWallet: credit.creditAddedToWallet,
    // Enrollment preview is source of truth for amount due (includes discounts).
    amountToBeCharged: hasEnrollmentAmount
      ? enrollmentAmountDue
      : credit.amountToBeCharged,
    currentTuitionFee: credit.currentTuitionFee,
    newTuitionFee: credit.newTuitionFee,
    billingIntent: credit.effectiveBillingIntent,
    prorationDisabledAtStudioLevel: credit.prorationDisabledAtStudioLevel,
    previewSummary: credit.previewSummary,
    discountTotal: discountTotal || undefined,
    discountLines: discountLines.length > 0 ? discountLines : undefined,
    transferCreditAppliedFromEnrollment:
      firstCharge?.transferCreditApplied ?? undefined,
    amountDueFromEnrollmentPreview: hasEnrollmentAmount,
  };
};

const getRemainingCreditV1API = async (
  params: GetRemainingCreditParams,
): Promise<GetRemainingCreditResponse> => {
  const response = await baseAPI.post<GetRemainingCreditResponse>(
    `/payment-processor/subscription/get-remaining-credit`,
    {
      currentClassId: params.currentClassId,
      newClassId: params.newClassId,
      studentId: params.studentId,
      billingIntent: params.billingIntent ?? BillingIntent.NO_CHANGE,
    },
  );
  return response.data;
};

const getRemainingCreditV2API = async (
  params: GetRemainingCreditParams,
): Promise<GetRemainingCreditResponse> => {
  const response = await baseAPI.post<V2TransferPreviewResponse>(
    `/v2/class-transfers/preview`,
    {
      studioId: params.studioId,
      studentId: params.studentId,
      currentClassId: params.currentClassId,
      newClassId: params.newClassId,
      billingIntent: params.billingIntent ?? BillingIntent.NO_CHANGE,
      ...(params.creditMode ? { creditMode: params.creditMode } : {}),
      ...(params.customCreditAmount !== undefined
        ? { customCreditAmount: params.customCreditAmount }
        : {}),
      ...(params.skipPaymentMethod !== undefined
        ? { skipPaymentMethod: params.skipPaymentMethod }
        : {}),
      ...(params.paymentMethodId
        ? { paymentMethodId: params.paymentMethodId }
        : {}),
      ...(params.paymentProvider
        ? { paymentProvider: params.paymentProvider }
        : {}),
      chargeMembershipFee: params.chargeMembershipFee === true,
      chargeCostumeFee: params.chargeCostumeFee === true,
    },
  );
  return mapV2PreviewToRemainingCredit(response.data);
};

export const getRemainingCreditAPI = async (
  params: GetRemainingCreditParams,
) => {
  if (params.isV2) {
    return getRemainingCreditV2API(params);
  }
  return getRemainingCreditV1API(params);
};

export const getRemainingCredit = (param: GetRemainingCreditParams) =>
  queryOptions({
    queryKey: [
      KEYS.REMAINING_CREDIT,
      param.isV2 === true,
      param.studioId,
      param.currentClassId,
      param.newClassId,
      param.studentId,
      param.billingIntent ?? BillingIntent.NO_CHANGE,
      param.creditMode ?? null,
      param.customCreditAmount ?? null,
      param.skipPaymentMethod ?? false,
      param.paymentMethodId ?? null,
      param.paymentProvider ?? null,
      param.chargeMembershipFee === true,
      param.chargeCostumeFee === true,
    ],
    queryFn: () => getRemainingCreditAPI(param),
    staleTime: 1000 * 60 * 5,
    enabled:
      !!param.studioId &&
      !!param.currentClassId &&
      !!param.newClassId &&
      !!param.studentId,
  });

export const useGetRemainingCredit = (params: GetRemainingCreditParams) => {
  return useQuery(getRemainingCredit(params));
};
